import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Polls /api/WorkflowAutomations/scheduler-status and returns a live
 * countdown (in seconds) to the next automation evaluation cycle.
 *
 * Shared by ActionItemAutomationsTab and the board-level debug chip.
 */
const useSchedulerCountdown = (apiFetch) => {
    const [nextRunAt, setNextRunAt] = useState(null);
    const [secondsRemaining, setSecondsRemaining] = useState(null);
    const [intervalSec, setIntervalSec] = useState(300);
    const timerRef = useRef(null);

    const fetchStatus = useCallback(async () => {
        try {
            const res = await apiFetch('/api/WorkflowAutomations/scheduler-status');
            const data = await res.json();
            if (data.success && data.data?.nextRunAtUtc) {
                const raw = data.data.nextRunAtUtc;
                const utcStr = typeof raw === 'string' && !raw.endsWith('Z') ? raw + 'Z' : raw;
                setNextRunAt(new Date(utcStr));
                if (data.data.intervalSeconds) setIntervalSec(data.data.intervalSeconds);
            }
        } catch {
            // Non-critical — timer just won't display
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    useEffect(() => {
        if (!nextRunAt) return;

        const tick = () => {
            const diff = Math.max(0, Math.round((nextRunAt.getTime() - Date.now()) / 1000));
            setSecondsRemaining(diff);
            if (diff === 0) {
                setTimeout(fetchStatus, 3000);
            }
        };

        tick();
        timerRef.current = setInterval(tick, 1000);
        return () => clearInterval(timerRef.current);
    }, [nextRunAt, fetchStatus]);

    const formatCountdown = (secs) => {
        if (secs == null) return null;
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    return {
        secondsRemaining,
        formatted: formatCountdown(secondsRemaining),
        intervalSec,
        isRunningNow: secondsRemaining === 0,
    };
};

export default useSchedulerCountdown;